import { StackActions } from '@react-navigation/native';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList } from 'react-native';

import { WebModal } from '@components/Modals/WebModal';
import { Box, Text } from '@components/Restyle';
import SearchInput from '@components/shared/SearchInput/SearchInput';
import {
  SkeletonList,
  TaskSearchResultSkeleton,
} from '@components/shared/skeletons';
import { TaskSearchResult } from '@components/Tasks/TaskSearchResult';
import { useSearchLazyQuery, SearchableResult } from '@graphql/generated';
import { useDebouncedSearch } from '@hooks/useDebouncedSearch';
import { useMinimumVisible } from '@hooks/useMinimumVisible';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { Search, SearchResultType } from '@root/types';
import { isTaskSearchResult } from '@utils/typeGuards';

const renderTaskCopySearchSkeletonRow = (index: number) => (
  <Box key={index} paddingHorizontal='m' marginTop='xs'>
    <TaskSearchResultSkeleton />
  </Box>
);

export const TaskCopySearch = () => {
  const navigation = useAppNavigation();
  const { t } = useTranslation();
  const [searchVal, setSearchVal] = useState('');

  const [globalSearch, { data, loading, variables: searchVariables }] =
    useSearchLazyQuery();
  const searchData = (data?.search as Search<SearchableResult>[]) || [];
  const initialResults = {
    messages: [],
    tasks: [],
    documents: [],
    projects: [],
  };

  const results =
    searchData.reduce<SearchResultType>((previous, result) => {
      if (isTaskSearchResult(result))
        return { ...previous, tasks: [...previous.tasks, result] };

      return previous;
    }, initialResults) || initialResults;

  const handleSearch = useCallback(
    (term: string) => {
      globalSearch({
        variables: {
          term,
          size: 20,
          includeMessages: false,
          includeDocuments: false,
          includeProjects: false,
          includeTasks: true,
        },
      });
    },
    [globalSearch]
  );

  const { isPending, isBelowThreshold, hasSearched, gatedTerm } =
    useDebouncedSearch(searchVal, handleSearch);
  // isPending settles to false one render before onSearch actually fires
  // (useDebouncedSearch calls it from an effect) — hasSearched closes that
  // gap so the empty state can't flash before the query is kicked off.
  const isLoading = loading || isPending || (!isBelowThreshold && !hasSearched);
  // The lazy query's `data`/`variables` only update once a fetch resolves —
  // while a newer term is debouncing or in flight, they still reflect the
  // PREVIOUS term's results, which would otherwise render under/instead of
  // the loading state.
  const isStaleTermResult =
    !isBelowThreshold && searchVariables?.term !== gatedTerm;
  const showSkeleton = useMinimumVisible(isLoading || isStaleTermResult);

  const taskListData =
    isBelowThreshold || isStaleTermResult
      ? []
      : results.tasks.map((r) => r.record);

  return (
    <WebModal
      visible
      title={t('shared:copy')}
      onClose={navigation.goBack}
      width={504}
      height={512}>
      <>
        <Box
          flexDirection='row'
          alignItems='center'
          marginTop='m'
          marginHorizontal='m'>
          <SearchInput
            onTextChanged={(newText) => setSearchVal(newText)}
            value={searchVal}
            loading={isLoading}
          />
        </Box>
        {isBelowThreshold ? (
          <Text variant='body' color='grey04' textAlign='center' mt='m'>
            {t('models:tasks.copy.searchCta')}
          </Text>
        ) : (
          <>
            {showSkeleton ? (
              <SkeletonList
                testID='task-copy-search-skeleton'
                row={renderTaskCopySearchSkeletonRow}
              />
            ) : (
              <FlatList
                showsVerticalScrollIndicator={false}
                ListEmptyComponent={
                  <Box mt='xs' marginHorizontal='m'>
                    <Text color='grey04' variant='body' textAlign='center'>
                      {t('models:tasks.copy.noResults')}
                    </Text>
                  </Box>
                }
                data={taskListData}
                renderItem={({ item }) => (
                  <Box paddingHorizontal='m'>
                    <TaskSearchResult
                      task={item}
                      searchValue={searchVal}
                      onPress={() => {
                        navigation.dispatch(
                          StackActions.replace('task-copy-select', {
                            taskId: item.id,
                          })
                        );
                      }}
                    />
                  </Box>
                )}
                ListFooterComponent={<Box mb='m' />}
                ListHeaderComponent={<Box mt='m' />}
              />
            )}
          </>
        )}
      </>
    </WebModal>
  );
};
